import React, { useState, useEffect, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { 
  FiLoader, FiSearch, FiEye, FiEyeOff, FiImage, 
  FiFlag, FiTrash2, FiCheckSquare, FiSquare, FiChevronDown
} = FiIcons;

export default function PlatformAdminContentTable() {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkActionOpen, setBulkActionOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('d2f_drops')
        .select(`*, d2f_artists ( name )`)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setContent(data || []);
    } catch (err) {
      console.error('[ContentTable] Error fetching content:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const filteredContent = content.filter(item => 
    (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.d2f_artists?.name && item.d2f_artists.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleSelectAll = () => {
    if (selectedItems.size === filteredContent.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredContent.map(c => c.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSet = new Set(selectedItems);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedItems(newSet);
  };

  const handleBulkAction = async (action) => {
    if (selectedItems.size === 0) return;
    setProcessing(true);
    setBulkActionOpen(false);
    
    const ids = Array.from(selectedItems);
    try {
      if (action === 'publish') {
        await supabase.from('d2f_drops').update({ is_published: true }).in('id', ids);
      } else if (action === 'unpublish') {
        await supabase.from('d2f_drops').update({ is_published: false }).in('id', ids);
      } else if (action === 'delete') {
        if(window.confirm(`Are you sure you want to delete ${ids.length} items?`)) {
          await supabase.from('d2f_drops').delete().in('id', ids);
        }
      } else if (action === 'clear_flags') {
        await supabase.from('d2f_drops').update({ is_flagged: false, report_count: 0 }).in('id', ids);
      }
      setSelectedItems(new Set());
      await fetchContent();
    } catch (err) {
      console.error('Bulk action failed:', err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center bg-neutral-900 border border-neutral-800 p-4 rounded-xl flex-wrap gap-4">
        <div className="relative w-full max-w-md">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            type="text"
            placeholder="Search drops by title or artist..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-black border border-neutral-800 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors"
          />
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-sm text-neutral-400">
            {selectedItems.size} selected
          </div>
          
          <div className="relative">
            <button 
              onClick={() => setBulkActionOpen(!bulkActionOpen)}
              disabled={selectedItems.size === 0 || processing}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
            >
              Bulk Actions <SafeIcon icon={FiChevronDown} />
            </button>
            
            {bulkActionOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-neutral-800 border border-neutral-700 rounded-xl shadow-xl overflow-hidden z-20">
                <button onClick={() => handleBulkAction('publish')} className="flex items-center gap-2 w-full px-4 py-3 text-sm text-left text-white hover:bg-neutral-700">
                  <SafeIcon icon={FiEye} className="text-green-400" /> Publish Selected
                </button>
                <button onClick={() => handleBulkAction('unpublish')} className="flex items-center gap-2 w-full px-4 py-3 text-sm text-left text-white hover:bg-neutral-700">
                  <SafeIcon icon={FiEyeOff} className="text-amber-400" /> Unpublish Selected
                </button>
                <button onClick={() => handleBulkAction('clear_flags')} className="flex items-center gap-2 w-full px-4 py-3 text-sm text-left text-white hover:bg-neutral-700">
                  <SafeIcon icon={FiFlag} className="text-blue-400" /> Clear Flags
                </button>
                <div className="h-px bg-neutral-700 w-full" />
                <button onClick={() => handleBulkAction('delete')} className="flex items-center gap-2 w-full px-4 py-3 text-sm text-left text-red-400 hover:bg-neutral-700">
                  <SafeIcon icon={FiTrash2} /> Delete Selected
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden">
        {loading || processing ? (
          <div className="flex items-center justify-center p-12 text-neutral-400">
            <SafeIcon icon={FiLoader} className="animate-spin text-2xl mr-3" />
            {processing ? 'Processing bulk action...' : 'Loading content library...'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-black/50 text-neutral-400">
                <tr>
                  <th className="px-4 py-4 w-12">
                    <button onClick={toggleSelectAll} className="text-lg hover:text-white transition-colors">
                      <SafeIcon icon={selectedItems.size === filteredContent.length && filteredContent.length > 0 ? FiCheckSquare : FiSquare} />
                    </button>
                  </th>
                  <th className="px-6 py-4 font-medium">Release</th>
                  <th className="px-6 py-4 font-medium">Artist</th>
                  <th className="px-6 py-4 font-medium">Type & Price</th>
                  <th className="px-6 py-4 font-medium">Status & Flags</th>
                  <th className="px-6 py-4 font-medium">Date Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {filteredContent.map(item => (
                  <tr key={item.id} className={`transition-colors ${selectedItems.has(item.id) ? 'bg-indigo-500/10' : 'hover:bg-white/5'}`}>
                    <td className="px-4 py-4">
                      <button onClick={() => toggleSelect(item.id)} className={`text-lg transition-colors ${selectedItems.has(item.id) ? 'text-indigo-400' : 'text-neutral-600 hover:text-white'}`}>
                        <SafeIcon icon={selectedItems.has(item.id) ? FiCheckSquare : FiSquare} />
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-neutral-800 flex items-center justify-center overflow-hidden flex-shrink-0 border border-neutral-700">
                          {item.cover_url ? (
                            <img src={item.cover_url} alt={item.title} className="w-full h-full object-cover" />
                          ) : (
                            <SafeIcon icon={FiImage} className="text-neutral-500" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-white max-w-[200px] truncate" title={item.title}>
                            {item.title || 'Untitled Release'}
                          </div>
                          <div className="text-xs text-neutral-500">{item.id.substring(0,8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-neutral-300 font-medium">{item.d2f_artists?.name || 'Unknown Artist'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-neutral-800 text-neutral-300 w-fit uppercase">
                          {item.drop_type || 'DROP'}
                        </span>
                        <div className="text-xs text-neutral-400">
                          {Number(item.min_price) > 0 ? `$${item.min_price}` : 'Free / PWYW'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2">
                        <div className={`flex items-center gap-1.5 text-xs font-medium ${item.is_published ? 'text-green-400' : 'text-neutral-500'}`}>
                          <SafeIcon icon={item.is_published ? FiEye : FiEyeOff} />
                          {item.is_published ? 'Published' : 'Draft'}
                        </div>
                        {(item.is_flagged || item.report_count > 0) && (
                          <div className="flex items-center gap-1.5 text-xs font-medium text-red-400 bg-red-400/10 px-2 py-1 rounded w-fit">
                            <SafeIcon icon={FiFlag} />
                            Flagged ({item.report_count || 1})
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-neutral-400">
                      <div className="text-xs">{new Date(item.created_at).toLocaleDateString()}</div>
                    </td>
                  </tr>
                ))}
                {filteredContent.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-neutral-500">
                      No content found matching your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}